import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';

import { MatIcon } from '@angular/material/icon';

import { ChipTone } from './guarantee-vocabulary';

/** One tone vocabulary for the whole module: a callout says warning the way a chip does. */
const TONE_CLASS: Record<ChipTone, string> = {
  neutral: 'bg-neutral-50 text-neutral-600',
  primary: 'bg-primary-50 text-primary-800',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50/60 text-warning-800',
  error: 'bg-error-50 text-error-700',
  info: 'bg-info-50 text-info-700',
};

/** Drawn only where the note has to be found rather than read once the eye reaches it. */
const BORDER_CLASS: Record<ChipTone, string> = {
  neutral: 'border border-neutral-200',
  primary: 'border border-primary-300',
  success: 'border border-success-300',
  warning: 'border border-warning-300',
  error: 'border border-error-300',
  info: 'border border-info-300',
};

const ICON_TONE_CLASS: Record<ChipTone, string> = {
  neutral: 'text-neutral-400',
  primary: 'text-primary-600',
  success: 'text-success-600',
  warning: 'text-warning-700',
  error: 'text-error-600',
  info: 'text-info-600',
};

/**
 * A standing note beside the thing it is about — why a step cannot be taken, or what a read failed
 * at. Not a toast: it belongs to the form it appears in and stays as long as the condition does.
 *
 * Content is projected rather than passed, so a callout can carry a retry button or a list of
 * reasons without a second component for each shape.
 */
@Component({
  selector: 'guarantees-callout',
  standalone: true,
  imports: [MatIcon],
  host: { class: 'block' },
  template: `
    <div class="flex items-start rounded-lg px-2 py-1.5 text-sm" [class]="toneClass()">
      @if (icon()) {
        <mat-icon class="mt-px mr-1.5 h-4! w-4! shrink-0 text-base! leading-4!" [class]="iconClass()">{{ icon() }}</mat-icon>
      }
      <span class="min-w-0 flex-1" [class]="stack() ? 'flex flex-col gap-1' : ''"><ng-content /></span>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutComponent {
  tone = input<ChipTone>('neutral');
  icon = input<string>('');
  /** Stacks the projected content instead of running it inline — for a note that carries a list. */
  stack = input(false, { transform: booleanAttribute });
  /** Outlines the note, for a condition that has to be noticed rather than come across. */
  border = input(false, { transform: booleanAttribute });

  protected readonly toneClass = computed(() => `${TONE_CLASS[this.tone()]} ${this.border() ? BORDER_CLASS[this.tone()] : ''}`);
  protected readonly iconClass = computed(() => ICON_TONE_CLASS[this.tone()]);
}
